import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { RequireAuth } from './auth/RequireAuth.tsx';
import { EnvironmentBanner } from './components/EnvironmentBanner.tsx';
import { AccessPage } from './routes/AccessPage.tsx';
import {
  AestheticsPage,
  AnalyticsPage,
  AuditPage,
  DermatologyPage,
  GastroenterologyPage,
  OphthalmologyPage,
  OrthopedicsPage,
  PediatricsPage,
  PhysicalTherapyPage,
} from './routes/GatedPages.tsx';
import { FormTemplatesPage } from './routes/FormTemplatesPage.tsx';
import { HelpPage } from './routes/HelpPage.tsx';
import { InboxPage } from './routes/InboxPage.tsx';
import { KioskCheckinPage } from './routes/kiosk/KioskCheckinPage.tsx';
import { LicenseScanCapturePage } from './routes/kiosk/LicenseScanCapturePage.tsx';
import { LoginPage } from './routes/LoginPage.tsx';
import { NotFoundPage } from './routes/NotFoundPage.tsx';
import { OverviewPage } from './routes/OverviewPage.tsx';
import { PatientChartPage } from './routes/patients/PatientChartPage.tsx';
import { PatientAestheticsPage } from './routes/PatientAestheticsPage.tsx';
import { PatientDuplicatesPage } from './routes/patients/PatientDuplicatesPage.tsx';
import { PatientLicenseScanReviewPage } from './routes/patients/PatientLicenseScanReviewPage.tsx';
import { PatientFormsPage } from './routes/PatientFormsPage.tsx';
import { PatientRcmChargesPage } from './routes/PatientRcmChargesPage.tsx';
import { PatientDermatologyPage } from './routes/PatientDermatologyPage.tsx';
import { PatientGastroenterologyPage } from './routes/PatientGastroenterologyPage.tsx';
import { PatientOrthopedicsPage } from './routes/PatientOrthopedicsPage.tsx';
import { PatientPediatricsPage } from './routes/PatientPediatricsPage.tsx';
import { PatientPhysicalTherapyPage } from './routes/PatientPhysicalTherapyPage.tsx';
import { PatientListPage } from './routes/patients/PatientListPage.tsx';
import { PatientStatementPage } from './routes/PatientStatementPage.tsx';
import { PlatformPage } from './routes/PlatformPage.tsx';
import { PracticeBrandingPage } from './routes/PracticeBrandingPage.tsx';
import { ProviderCalendarSyncPage } from './routes/ProviderCalendarSyncPage.tsx';
import { ResellerRollupPage } from './routes/ResellerRollupPage.tsx';
import { SchedulePage } from './routes/SchedulePage.tsx';
import { SignaturePage } from './routes/SignaturePage.tsx';
import { SpecialtyModulesPage } from './routes/SpecialtyModulesPage.tsx';
import { AppShell } from './shell/AppShell.tsx';
import { PortalAccountPage } from './patientPortal/routes/PortalAccountPage.tsx';
import { PortalHelpPage } from './patientPortal/routes/PortalHelpPage.tsx';
import { PortalHomePage } from './patientPortal/routes/PortalHomePage.tsx';
import { PortalLoginPage } from './patientPortal/routes/PortalLoginPage.tsx';
import { PortalMessagesPage } from './patientPortal/routes/PortalMessagesPage.tsx';
import { PortalRecordsPage } from './patientPortal/routes/PortalRecordsPage.tsx';
import { PortalRegisterPage } from './patientPortal/routes/PortalRegisterPage.tsx';
import { PortalSchedulePage } from './patientPortal/routes/PortalSchedulePage.tsx';
import { RequirePatientSession } from './patientPortal/session/RequirePatientSession.tsx';
import { PortalShell } from './patientPortal/shell/PortalShell.tsx';

/**
 * Routes.
 *
 * `<BrowserRouter>` + declarative `<Routes>`, per `docs/roadmap.md` §2's "React Router declarative
 * mode": public routes are flat siblings, and everything authenticated hangs off one layout route
 * wrapped in the guard.
 *
 * Structuring it this way is what makes the guard unavoidable. A new authenticated page is a
 * `<Route>` nested inside the wrapped layout; adding one *outside* it is a visible, deliberate
 * change to this file rather than a forgotten call. That is the client-side echo of
 * `server/src/app.ts`'s deny-by-default gate — a hole here requires an omission someone can see in
 * a diff.
 */
export function App() {
  return (
    <BrowserRouter>
      {/* Outside <Routes> so it is present on every page, signed in or not — including the login
          page, which is where someone about to type real credentials into a demo needs it most.
          It is the first element in the document, so it is also the first thing a screen reader
          reaches. */}
      <div className="flex min-h-dvh flex-col bg-canvas">
        <EnvironmentBanner />
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          {/* Public, deliberately outside `RequireAuth` — `HelpPage.tsx`'s own header comment
              explains why: no PHI, no API call, and a person may want it (or a support link may
              send them to it) before they have ever signed in. Two routes, one element, rather
              than a single `:topicId?` optional-segment route — explicit and easy to scan,
              matching how every other route in this file is declared one at a time. */}
          <Route path="/help" element={<HelpPage />} />
          <Route path="/help/:topicId" element={<HelpPage />} />

          {/* Kiosk/tablet check-in (`BACKLOG.md` C7) — public in the identical sense `/login` is:
              no staff session, ever. Real auth is the bearer token `KioskCheckinPage.tsx` reads from
              its own URL fragment, checked by the server's `requireKioskToken`, not by anything this
              router does — this route exists outside `RequireAuth` because there is no staff
              identity to require here, not because nothing is checked. */}
          <Route path="/kiosk" element={<KioskCheckinPage />} />

          {/* Driver's-license barcode scan capture (`BACKLOG.md` C19,
              `docs/decisions/patient-license-scan-scope.md`) — the identical "public route, different
              real auth" shape `/kiosk` above establishes: no staff session, ever. Real auth is the
              `license_scan`-purpose bearer token `LicenseScanCapturePage.tsx` reads from its own URL
              fragment, checked by the server's `requireLicenseScanToken`. */}
          <Route path="/kiosk/license-scan" element={<LicenseScanCapturePage />} />

          {/* The patient portal (`BACKLOG.md` W4, `docs/decisions/patient-portal-frontend-scope.md`)
              — a genuinely separate route tree from the staff app above, matching the routing design
              call that doc records: its own auth surface (`RequirePatientSession`, a distinct cookie
              from the staff session `RequireAuth` above checks), its own shell (`PortalShell`), and a
              route prefix (`/portal/:orgId/*`) that cannot collide with any staff route in this file.
              `orgId` travels in every path here, not just the protected ones, because the portal is
              reached at an org-specific entry point (`docs/decisions/patient-portal-scope.md`'s own
              framing) and there is no session yet at `/login`/`/register` to read it from. */}
          <Route path="/portal/:orgId/login" element={<PortalLoginPage />} />
          <Route path="/portal/:orgId/register" element={<PortalRegisterPage />} />

          {/* The portal's own in-app help (`BACKLOG.md` W6) — public in the identical sense
              `/help`/`/help/:topicId` are for the staff app (`HelpPage.tsx`'s own comment): no PHI,
              no API call, static prose, so a patient can reach it (via a `PortalHelpLink`) before
              they have ever signed in — exactly where "why didn't my code arrive" comes up. Two
              routes, one element, matching how the staff pair above is declared. */}
          <Route path="/portal/:orgId/help" element={<PortalHelpPage />} />
          <Route path="/portal/:orgId/help/:topicId" element={<PortalHelpPage />} />
          <Route
            path="/portal/:orgId"
            element={
              <RequirePatientSession>
                <PortalShell />
              </RequirePatientSession>
            }
          >
            <Route index element={<PortalHomePage />} />
            <Route path="records" element={<PortalRecordsPage />} />
            <Route path="schedule" element={<PortalSchedulePage />} />
            <Route path="messages" element={<PortalMessagesPage />} />
            <Route path="account" element={<PortalAccountPage />} />
          </Route>

          <Route
            path="/"
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          >
            <Route index element={<OverviewPage />} />
            <Route path="patients" element={<PatientListPage />} />
            {/* Duplicate-match review queue (BACKLOG.md C2a) — a static sibling of the dynamic
              `:patientId` route below. React Router ranks a static segment ahead of a dynamic one
              regardless of declaration order, so this never gets shadowed by `:patientId`
              matching "duplicates" as an id. */}
            <Route path="patients/duplicates" element={<PatientDuplicatesPage />} />
            <Route path="patients/:patientId" element={<PatientChartPage />} />
            {/* Driver's-license scan staff review queue (BACKLOG.md C19) — a 3-segment path, so it
              never shares `patients/duplicates`' own static-vs-dynamic ambiguity with the
              `:patientId` route above; reachable from `PatientChartPage.tsx`'s own
              `LicenseScanTokenPanel`. */}
            <Route
              path="patients/:patientId/license-scans"
              element={<PatientLicenseScanReviewPage />}
            />
            <Route path="access" element={<AccessPage />} />
            <Route path="signature" element={<SignaturePage />} />
            <Route path="branding" element={<PracticeBrandingPage />} />
            <Route path="calendar-sync" element={<ProviderCalendarSyncPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="audit" element={<AuditPage />} />
            <Route path="platform" element={<PlatformPage />} />
            {/* Specialty-module catalog (BACKLOG.md M3/M5-M22) — dermatology plus eighteen
              "coming soon" placeholders. The real dermatology surface is patient-scoped, below,
              and reachable directly even with this nav item's own row showing an upgrade prompt
              for an unlicensed org, per GatedPages.tsx's "a route cannot rely on nav" reasoning. */}
            <Route path="specialty-modules" element={<SpecialtyModulesPage />} />
            <Route path="dermatology" element={<DermatologyPage />} />
            <Route path="patients/:patientId/dermatology" element={<PatientDermatologyPage />} />
            <Route path="aesthetics" element={<AestheticsPage />} />
            <Route path="patients/:patientId/aesthetics" element={<PatientAestheticsPage />} />
            <Route path="physical-therapy" element={<PhysicalTherapyPage />} />
            <Route
              path="patients/:patientId/physical-therapy"
              element={<PatientPhysicalTherapyPage />}
            />
            <Route path="pediatrics" element={<PediatricsPage />} />
            <Route path="patients/:patientId/pediatrics" element={<PatientPediatricsPage />} />
            <Route path="gastroenterology" element={<GastroenterologyPage />} />
            <Route
              path="patients/:patientId/gastroenterology"
              element={<PatientGastroenterologyPage />}
            />
            <Route path="orthopedics" element={<OrthopedicsPage />} />
            <Route path="patients/:patientId/orthopedics" element={<PatientOrthopedicsPage />} />
            {/* Ophthalmology (BACKLOG.md M10) — unlike every sibling above, there is no
              patients/:patientId/ophthalmology route yet: the server side is fully built, but no
              per-patient chart page consumes it (OphthalmologyPage's own header explains why this
              is stated plainly rather than routed to a nonexistent page). */}
            <Route path="ophthalmology" element={<OphthalmologyPage />} />
            <Route path="patients/:patientId/rcm/charges" element={<PatientRcmChargesPage />} />
            {/* RCM patient statement (BACKLOG.md R6) — ordinary chart content, not a specialty
              module, so it needs no GatedPages.tsx catalog entry the way dermatology/aesthetics
              have; PatientChartPage.tsx's tabs row links here directly. */}
            <Route path="patients/:patientId/statement" element={<PatientStatementPage />} />
            {/* General-purpose clinical form/template builder v1 (BACKLOG.md C10b). The fill/list
              surface is patient-scoped, reachable from PatientChartPage.tsx's own tabs row; the
              admin builder below is a top-level, org-scoped page (org:manage_settings). */}
            <Route path="patients/:patientId/forms" element={<PatientFormsPage />} />
            <Route path="form-templates" element={<FormTemplatesPage />} />
            {/* Roll-up billing across a reseller's managed children (BACKLOG.md T17) — reachable
              directly by URL like every other GatedRoute page; shell/navigation.ts's own nav entry
              hides it from anyone without org:read_children, but the route itself re-checks. */}
            <Route path="reseller/rollup-statement" element={<ResellerRollupPage />} />
            <Route path="schedule" element={<SchedulePage />} />
            {/* Staff secure-messaging inbox (BACKLOG.md W2, pillar 3) — a cross-patient work queue,
              a static top-level sibling of /schedule rather than a chart tab, matching the decision
              doc's own "Staff-side UI implication" section. */}
            <Route path="inbox" element={<InboxPage />} />
            {/* Inside the guard, so an unknown path does not disclose whether it exists to someone
              who is not signed in. */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </div>
    </BrowserRouter>
  );
}
